import { useCallback, useEffect, useState } from 'react';
import { toOutcome, useSession, type Outcome } from '@huaca/ui';
import { getPosting } from './api';
import { formatDay } from './deadline';
import { DETAIL_HEADINGS, KIND_LABELS, STATUS_LABELS } from './labels';
import { EMPLOYMENT_TYPE_LABELS, REGION_LABELS, TENDER_TYPE_LABELS } from './taxonomy';
import type { PostingDetail } from './types';

/**
 * One posting, whole.
 *
 * SPLIT INTO A HOOK AND A BODY, with no page chrome of its own. A posting is only ever read inside
 * the side panel now — there is no standalone route left — so the back button, the outcome frame
 * and the page wrapper belong to whatever is presenting it rather than to the content.
 */

/** In the order a reader wants them, which is not the order the contract declares them. */
const HEADING_ORDER = Object.keys(DETAIL_HEADINGS) as (keyof typeof DETAIL_HEADINGS)[];

export interface PostingLoad {
  posting: PostingDetail | null;
  outcome: Outcome | null;
  reload: () => void;
}

export function usePosting(slug: string, siteId: string): PostingLoad {
  const { client } = useSession();
  const [posting, setPosting] = useState<PostingDetail | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const load = useCallback(async () => {
    setOutcome(null);
    // Cleared FIRST, so opening a second posting shows a skeleton rather than the previous
    // posting's text under the new one's title.
    setPosting(null);
    try {
      setPosting(await getPosting(client, slug, siteId));
    } catch (err) {
      setOutcome(toOutcome(err));
    }
  }, [client, siteId, slug]);

  useEffect(() => {
    void load();
  }, [load]);

  return { posting, outcome, reload: () => void load() };
}

export function PostingBody({ posting }: { posting: PostingDetail }) {
  return (
    <article className="tenders-detail">
      <h1>{posting.title}</h1>

      <p className="tenders-card__meta">
        <span className="badge">{KIND_LABELS[posting.kind]}</span>
        <span className="badge">{STATUS_LABELS[posting.status]}</span>
        {posting.categoryLabel && <span className="badge">{posting.categoryLabel}</span>}
        {posting.region && <span className="badge">{REGION_LABELS[posting.region]}</span>}
        {posting.tenderType && <span className="badge">{TENDER_TYPE_LABELS[posting.tenderType]}</span>}
        {posting.employmentType && <span className="badge">{EMPLOYMENT_TYPE_LABELS[posting.employmentType]}</span>}
      </p>

      <dl className="tenders-facts">
        <dt>מפרסם</dt>
        <dd>{posting.source.publisher}</dd>
        <dt>מועד אחרון להגשה</dt>
        <dd>{formatDay(posting.dates.lastSubmissionDate) ?? 'לא צוין'}</dd>
        <dt>מועד אחרון לשאלות הבהרה</dt>
        <dd>{formatDay(posting.dates.clarification) ?? 'לא צוין'}</dd>
        {posting.value && (
          <>
            <dt>היקף כספי</dt>
            <dd>
              <bdi>
                {posting.value.amount.toLocaleString('he-IL')} {posting.value.currency}
              </bdi>
            </dd>
          </>
        )}
      </dl>

      {/* Only the fields the model actually filled. `null` means "the posting does not say",
          not "empty", so rendering an empty section would assert something false. */}
      {HEADING_ORDER.filter((key) => posting.details[key]).map((key) => (
        <section key={key} className="tenders-detail__section">
          <h2>{DETAIL_HEADINGS[key]}</h2>
          <p>{posting.details[key]}</p>
        </section>
      ))}

      {posting.meetings.length > 0 && (
        <section className="tenders-detail__section">
          <h2>מפגשים</h2>
          <ul>
            {posting.meetings.map((meeting, index) => (
              <li key={index}>
                {formatDay(meeting.date) ?? 'מועד לא צוין'}
                {meeting.location && ` · ${meeting.location}`}
              </li>
            ))}
          </ul>
        </section>
      )}

      {(posting.contact.name ?? posting.contact.email ?? posting.contact.phone) && (
        <section className="tenders-detail__section">
          <h2>איש קשר</h2>
          <p>
            {posting.contact.name}
            {posting.contact.email && (
              <>
                {' · '}
                <a href={`mailto:${posting.contact.email}`}>
                  <bdi>{posting.contact.email}</bdi>
                </a>
              </>
            )}
            {posting.contact.phone && (
              <>
                {' · '}
                <bdi>{posting.contact.phone}</bdi>
              </>
            )}
          </p>
        </section>
      )}

      {posting.documents.length > 0 && (
        <section className="tenders-detail__section">
          <h2>מסמכים</h2>
          {/* Metadata only, linking OUT to the source portal. The extracted text lives in an
              ETL collection this product deliberately does not read, and no file is proxied
              or re-hosted here. */}
          <ul>
            {posting.documents.map((doc) => (
              <li key={doc.url}>
                <a href={doc.url} target="_blank" rel="noreferrer noopener">
                  {doc.name || doc.url}
                </a>
                {doc.pageCount > 0 && ` · ${doc.pageCount} עמ׳`}
              </li>
            ))}
          </ul>
        </section>
      )}

      {posting.source.detailLink && (
        <p>
          <a href={posting.source.detailLink} target="_blank" rel="noreferrer noopener">
            צפייה במקור באתר המפרסם
          </a>
        </p>
      )}
    </article>
  );
}
